/**
 * [Analytics Fixes 2026-07 Phase 1b - TD-83] AdpDisplay.
 *
 * Renders an ADP value, or the "UL" (Unlisted, for now) pill when the
 * player has no consensus ADP. Founder decision on NULL-ADP players
 * (R3, greenlight message 2026-07): keep them in Finds/Flags as
 * conviction calls - a deep player the market hasn't priced is exactly
 * the edge PGF exists to surface. The "for now" wording is load-bearing
 * per the founder brief: it signals the gap closes as PGF builds its
 * own draft data.
 *
 * Data source: Fantasy Football Calculator public API, refreshed nightly
 * by jobs/adp_refresh.py. TD-87 will replace FFC with PGF-native
 * draft-derived ADP + AAV once live draft volume permits.
 *
 * Props:
 *   value:       number | null | undefined   - the raw ADP (float)
 *   label:       string | null | undefined   - pre-formatted positional
 *                                              label like "WR12" or
 *                                              "#WR12", if the caller
 *                                              wants that shape instead
 *                                              of the raw value.
 *   prefix:      string - text placed before the value ("ADP #",
 *                         "Default: ADP ", etc.). Suppressed for UL.
 *   className:   string - extra classes on the wrapper span.
 *   testId:      string - data-testid for QA hooks.
 */
import { useState } from "react";

export function AdpDisplay({ value, label, prefix = "", className = "", testId }) {
  const hasValue = value != null || (label != null && label !== "");
  if (hasValue) {
    return (
      <span className={className} data-testid={testId || "adp-display"}>
        {prefix}{label != null && label !== "" ? label : value.toFixed(1)}
      </span>
    );
  }
  return <UnlistedPill className={className} testId={testId || "adp-unlisted"} />;
}

export function UnlistedPill({ className = "", testId = "adp-unlisted" }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <span
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      onTouchStart={() => setShowTip((s) => !s)}
      data-testid={testId}
    >
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-[#606060]/50 bg-[#2A2A2A] text-[#A0A0A0] text-[10px] font-display font-black uppercase tracking-widest cursor-help"
      >
        UL
      </span>
      {showTip && (
        <span
          role="tooltip"
          className="absolute z-50 left-1/2 -translate-x-1/2 top-[calc(100%+4px)] whitespace-nowrap px-2 py-1 rounded-md bg-black/90 border border-[#404040] text-[10px] text-[#F5F5F5] font-body pointer-events-none"
        >
          Unlisted (for now)
        </span>
      )}
    </span>
  );
}

export default AdpDisplay;
